import React, { createContext, useContext, useState, useCallback } from 'react';

export interface UnreadMessagesState {
  [contactId: number]: boolean;
}

interface AppLayoutContextType {
  contacts: any[];
  setContacts: React.Dispatch<React.SetStateAction<any[]>>;
  unreadMessages: UnreadMessagesState;
  setUnreadMessages: (state: UnreadMessagesState | ((prev: UnreadMessagesState) => UnreadMessagesState)) => void;
}

const AppLayoutContext = createContext<AppLayoutContextType | undefined>(undefined);

export const STORAGE_KEY = '@ligchat/unreadMessages';

export const useAppLayout = () => {
  const ctx = useContext(AppLayoutContext);
  if (!ctx) throw new Error('useAppLayout must be used within AppLayoutProvider');
  return ctx;
};

/** Provider que aceita value externo (usado pelo CombinedMenu) ou usa estado interno */
export const AppLayoutProvider: React.FC<{
  children: React.ReactNode;
  value?: AppLayoutContextType;
}> = ({ children, value: externalValue }) => {
  const [internalContacts, setInternalContacts] = useState<any[]>([]);
  const [internalUnread, setInternalUnreadState] = useState<UnreadMessagesState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const setInternalUnread = useCallback((newState: UnreadMessagesState | ((prev: UnreadMessagesState) => UnreadMessagesState)) => {
    setInternalUnreadState(prev => {
      const next = typeof newState === 'function' ? newState(prev) : newState;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, []);

  const internalValue: AppLayoutContextType = {
    contacts: internalContacts,
    setContacts: setInternalContacts,
    unreadMessages: internalUnread,
    setUnreadMessages: setInternalUnread
  };

  const value = externalValue ?? internalValue;

  return (
    <AppLayoutContext.Provider value={value}>
      {children}
    </AppLayoutContext.Provider>
  );
};
